import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@unistocks/components/toast';
import { invalidateNotificationsGetListQuery } from '@unistocks/features/notifications/invalidate-notification-list';
import { severityToNotificationType } from '@unistocks/features/notifications/utils';
import { NotificationDto } from '@unistocks/proxy/axios-client';
import { notificationsGetUnreadNotificationsCountQueryKey } from '@unistocks/proxy/axios-client/NotificationsQuery';
import { Logger } from '@unistocks/shared/services/logger.service';
import { NOTIFICATION_HUB, RECEIVE_NOTIFICATION } from '@unistocks/shared/signalr/consts';
import { useSignalRHub } from '@unistocks/shared/signalr/use-signalr-hub';
import { useCallback, useEffect } from 'react';

/**
 * Hook that listens to notification events from the SignalR hub
 * and displays notifications.
 */
export const useNotificationHub = () => {
  const connection = useSignalRHub(NOTIFICATION_HUB);
  const queryClient = useQueryClient();

  const handleEvent = useCallback(
    (notification: NotificationDto) => {
      Logger.debug('Received notification', notification.title, notification.message);

      invalidateNotificationsGetListQuery(queryClient);
      queryClient.invalidateQueries({
        queryKey: notificationsGetUnreadNotificationsCountQueryKey()
      });

      const type = severityToNotificationType[notification.severity];
      const toastFn =
        type === 'error'
          ? toast.error
          : type === 'warning'
            ? toast.warning
            : type === 'info'
              ? toast.info
              : toast.success;
      toastFn(notification.title, { description: notification.message || undefined });
    },
    [queryClient]
  );

  useEffect(() => {
    connection?.on(RECEIVE_NOTIFICATION, handleEvent);

    return () => {
      connection?.off(RECEIVE_NOTIFICATION);
    };
  }, [connection, handleEvent]);
};
